<template>
  <div>
    <!-- Фуллскрин баннер, если нет интернета -->
    <div v-if="isOffline" class="offline-overlay">
      <div class="offline-box">
        <div class="offline-title">{{ $t('no_network_connect') }}</div>
        <div class="offline-text">
          {{ $t('net_connect_message') }}
        </div>
      </div>
    </div>

    <!-- Гайд показываем только когда есть сеть -->
    <template v-if="showGuideTour && !isOffline">
      <GuideTour @action-success="handleGuideTourSuccess" />
    </template>

    <template v-else>
      <div
          class="min-h-screen safe-area-container"
          :class="[
          $theme.baseBg,
          [
            'setting',
            'stats',
            'home',
            'notifications',
            'signals',
            'signal_logs',
            'language',
            'orders',
            'refs',
            'faq',
            'tariff',
            'profile',
            'security',
            'show_signal',
            'ref_stats',
            'incomplete',
            'terms',
            'link-card',
            'payment_cards',
            'user_logs',
            'referral',
            'stats_exchange',
            'auth',
          ].includes($route.name)
            ? $theme.secondBaseBg
            : '',
          $theme.baseTextColor,
        ]"
      >
        <template v-if="isShowAlerts">
          <EmergencyAlerts :list_errors="readyTrading" />
        </template>

        <router-view v-slot="{ Component, route }">
          <transition name="fade" mode="out-in">
            <div :key="route.fullPath" class="safe-area-content">
              <component :is="Component" />
            </div>
          </transition>
        </router-view>
      </div>

      <template v-if="isShowNavbar">
        <AppNavbar class="safe-area-footer" />
      </template>
    </template>
  </div>
</template>

<script>
import GuideTour from '@/components/GuideTour.vue';
import AppNavbar from '@/components/AppNavbar.vue';
import EmergencyAlerts from '@/components/elem/EmergencyAlerts.vue';

export default {
  name: 'App',
  components: { AppNavbar, EmergencyAlerts, GuideTour },

  data() {
    return {
      telegramReady: false,
      readyTrading: {
        tariff_selected: false,
        tariff_expired: false,
        signals_disabled: false,
        balance_unavailable: false,
        exchange_disconnected: false,
      },
      unread_count: 0,
      showGuideTour: false,

      // PTR instance (у PullToRefresh.init() есть метод destroy())
      ptrInstance: null,

      // EDGE-only PTR
      lastTouchStartY: null,
      safeTopInset: 0,
      EDGE_PULL_PX: 28,

      // сеть
      isOffline: false,
    };
  },

  computed: {
    activeTheme() {
      return this.$user?.theme || 'dark';
    },
    isShowNavbar() {
      if (this.$user) {
        return (
            (this.$user.reg_status === 'COMPLETED' ||
                this.$user.reg_status === 'SKIP_SETUP') &&
            this.$route.name !== 'two-factor-auth' &&
            this.$route.name !== 'auth'
        );
      }
      return false;
    },
    enabledSignals() {
      return this.$store.state.enabledSignals;
    },
    isShowAlerts() {
      if (!this.isShowNavbar) return false;
      return (
          this.readyTrading.tariff_selected ||
          this.readyTrading.tariff_expired ||
          this.readyTrading.exchange_disconnected ||
          this.readyTrading.signals_disabled ||
          this.readyTrading.balance_unavailable
      );
    },
  },

  watch: {
    '$route.name'(name) {
      if (name === 'home') this.loadUnreadNotifications();
    },
    $user: {
      immediate: true,
      handler(user) {
        this.showGuideTour = !user;
      },
    },
    activeTheme: {
      immediate: true,
      async handler(theme) {
        localStorage.setItem('theme', theme || 'dark');
        this.applyHtmlBg(theme);
        await this.$nextTick();
      },
    },
  },

  methods: {
    handleGuideTourSuccess() {
      this.showGuideTour = false;
      this.$router.push({ name: 'login' });
    },

    loadUnreadNotifications() {
      this.$api
          .get('notifications/unread')
          .then((res) => (this.unread_count = res.data.unread_count))
          .catch((e) => console.error('Error loading notifications:', e));
    },

    checkReadyTrading() {
      this.$api
          .get('/user/ready-trading')
          .then(
              (result) =>
                  (this.readyTrading = { ...this.readyTrading, ...result.data }),
          )
          .catch((e) => console.error('Error checking trading readiness:', e));
    },

    applyHtmlBg(theme) {
      const isDark = (theme || 'dark') === 'dark';
      const root = document.documentElement;
      root.classList.toggle('dark', isDark);
      root.classList.toggle('light', !isDark);
      document.body.classList.toggle('dark', isDark);
      document.body.classList.toggle('light', !isDark);

      let meta = document.querySelector('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', isDark ? '#0c0c0c' : '#ffffff');

      const boot = document.getElementById('theme-instant');
      if (boot) boot.remove();
    },

    // ======= Сеть =======
    handleOnline() {
      this.isOffline = false;
    },
    handleOffline() {
      this.isOffline = true;
    },

    // ====== EDGE-ONLY логика ======
    computeSafeTopInset() {
      const probe = document.createElement('div');
      probe.style.position = 'absolute';
      probe.style.top = '0';
      probe.style.left = '0';
      probe.style.visibility = 'hidden';
      probe.style.paddingTop = 'env(safe-area-inset-top)';
      document.body.appendChild(probe);
      const px = parseFloat(getComputedStyle(probe).paddingTop) || 0;
      document.body.removeChild(probe);
      this.safeTopInset = px;
    },
    onTouchStart(e) {
      const t = e.touches && e.touches[0] ? e.touches[0] : null;
      this.lastTouchStartY = t ? t.clientY : null;
    },
    onPointerDown(e) {
      if (e.pointerType === 'touch') {
        this.lastTouchStartY = e.clientY;
      }
    },
    isEdgePull() {
      if (this.lastTouchStartY == null) return false;
      return this.lastTouchStartY <= this.safeTopInset + this.EDGE_PULL_PX;
    },
  },

  mounted() {
    this.applyHtmlBg(this.activeTheme);

    this.isOffline =
        typeof navigator !== 'undefined' ? !navigator.onLine : false;

    // safe-area
    this.computeSafeTopInset();
    window.addEventListener('touchstart', this.onTouchStart, {
      passive: true,
    });
    window.addEventListener('pointerdown', this.onPointerDown, {
      passive: true,
    });

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    if (this.$user) {
      this.$store.commit(
          'updateEnabledSignals',
          this.$user.settings.signals,
      );
      this.checkReadyTrading();
      this.loadUnreadNotifications();
    }
  },

  beforeUnmount() {
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  },
};
</script>

<style>
/* ===== Переменные темы ===== */
:root {
  --safe-area-inset-top: 0px;
  --safe-area-inset-bottom: 0px;
  --safe-area-inset-left: 0px;
  --safe-area-inset-right: 0px;

  /* дефолт до навешивания класса */
  --page-bg: #0c0c0c;
  --ptr-color: #fff;
}

html.dark {
  color-scheme: dark;
  --page-bg: #0c0c0c;
  --ptr-color: #fff;
}
html.light {
  color-scheme: light;
  --page-bg: #ffffff;
  --ptr-color: #111;
}

/* База */
html,
body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  background: var(--page-bg);
}
body {
  overscroll-behavior-y: contain;
  -webkit-tap-highlight-color: transparent;
}

.safe-area-container {
  min-height: 100vh;
  box-sizing: border-box;
  background: inherit;
}

/* Фолбэк на первый кадр */
html:not(.dark):not(.light),
body:not(.dark):not(.light) {
  background: #0c0c0c;
}
@media (prefers-color-scheme: light) {
  html:not(.dark):not(.light),
  body:not(.dark):not(.light) {
    background: #ffffff;
  }
}

/* Анимации */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* iOS safe-area */
@supports (padding-top: env(safe-area-inset-top)) {
  .safe-area-container {
    padding-top: env(safe-area-inset-top) !important;
    padding-bottom: calc(env(safe-area-inset-bottom) + 85px) !important;
    padding-left: env(safe-area-inset-left) !important;
    padding-right: env(safe-area-inset-right) !important;
  }
}

/* ==== Кастомизация pulltorefreshjs ==== */
.ptr--ptr,
.ptr--box,
.ptr--text,
.ptr--icon {
  color: var(--ptr-color) !important;
}

.ptr--icon svg,
.ptr--icon path,
.ptr--icon circle {
  stroke: currentColor !important;
  fill: none;
}

.ptr--ptr {
  z-index: 99999 !important;
}
.ptr--box {
  background: transparent !important;
}
.ptr--text {
  font-weight: 500;
}
.ptr--icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.ptr--icon svg {
  width: 20px;
  height: 20px;
}

/* ==== Оффлайн-оверлей ==== */
.offline-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000; /* выше PTR и всего остального */
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
}

.offline-box {
  max-width: 320px;
}

.offline-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.offline-text {
  font-size: 14px;
  opacity: 0.8;
}
</style>
